// 設備詳細頁各分頁的區塊（DEV-05 / DEV-10）：連線健康、AI、韌體、位置、稽核、複製給客服。資料都是既有 API；拿不到（沒權限 / 服務沒開）就說明，不顯示假資料。
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { PATH_SEP, siteNamePath } from '../../lib/orgRoot';
import { useConfirm } from '../../components/ConfirmDialog';
import { kinioFetch, planeBase } from '@kinio/web-sdk';
import type { VmsDevice } from '../../lib/devices';
import { useScope } from '../../lib/scope';
import { reporterOrgs } from '../../lib/supportApi';
import { aiApi, hasOrgPerm, orgApi, vmsApi, type MyOrg } from '../../lib/planeApi';
import { AuditRows, useAuditNames } from '../audit/AuditPage';
import { certState } from '../overview/health';
import { Time } from '../../components/Time';
import { useTzResolver } from '../../lib/useTz';
import { useDevicePath } from './path';
import { netifsOf, primaryMacOf } from './netifs';

/** 憑證遠期（9999-12-31 這類）= 不會過期（SPEC DEV-05 健康與韌體、DEV-06）。 */
const certNever = (s: string) => /^9{4}-/.test(s) || Date.parse(s) > Date.UTC(9000, 0, 1);

/**
 * 連線健康（DEV-10；P1-6）：在線 / 離線原因、最後連線、憑證到期（遠期寫「不會過期」）、Wi-Fi 訊號、機體溫度（shadow kinio_system）。
 * 沒有回報的列不畫（不佔版面放「—」）；時鐘偏移設備還沒有來源，不顯示。
 */
export function ConnectionHealth({ dev, reported }: { dev: VmsDevice; reported?: Record<string, unknown> }) {
  const { t } = useTranslation();
  const tzOf = useTzResolver();
  const sys = (reported?.kinio_system ?? {}) as Record<string, unknown>;
  const cert = certState(dev.certNotAfter, Date.now());
  const rows: [string, React.ReactNode, string?][] = [
    [
      t('ddev.status'),
      `${t(`devices.${dev.presence}`)}${dev.presence === 'offline' && dev.presenceReason ? ' · ' + t(`devices.reason_${dev.presenceReason}`, { defaultValue: t('devices.reason_other') }) : ''}`,
      dev.presence === 'offline' ? 'bad' : undefined,
    ],
  ];
  if (dev.lastSeen) rows.push([t('devices.col_last_seen'), <Time at={dev.lastSeen} tz={tzOf(dev.homeId)} />]);
  if (dev.certNotAfter)
    rows.push([
      t('dhealth.cert'),
      certNever(dev.certNotAfter)
        ? t('dset.val_never')
        : `${dev.certNotAfter.slice(0, 10)}${cert === 'expired' ? ' · ' + t('dhealth.cert_expired') : cert === 'soon' ? ' · ' + t('dhealth.cert_soon') : ''}`,
      cert === 'expired' ? 'bad' : cert === 'soon' ? 'warn' : undefined,
    ]);
  if (typeof sys.wifi_signal_percent === 'number')
    rows.push([t('dhealth.wifi'), `${sys.wifi_signal_percent}%`, sys.wifi_signal_percent < 30 ? 'warn' : undefined]);
  if (typeof sys.device_temperature === 'number') rows.push([t('dhealth.temperature'), `${sys.device_temperature} °C`]);
  // 主介面 MAC：平台只回給有管理權的人（read_authz，netifs.ts），沒有就不畫
  const mac = primaryMacOf(sys);
  if (mac) rows.push([t('dhealth.mac'), <code data-testid="conn-mac">{mac}</code>]);
  return (
    <table className="grid dinfo" data-testid="conn-health">
      <tbody>
        {rows.map(([k, v, lvl]) => (
          <tr key={k} data-level={lvl}>
            <th scope="row">{k}</th>
            <td className={lvl === 'bad' ? 'error' : lvl === 'warn' ? 'warn-text' : undefined}>{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * 網路介面（plan SPEC「設備資訊：區網位址與網路介面」）：kinio_system.netifs 每張實體介面一列（介面名、種類、MAC、連線、IP、IPv6）。
 * netifs 是 read_authz: owner——只有有管理權的人的 shadow 帶它；沒有（沒權限、舊韌體還沒報）整區不出現。
 */
export function NetworkInterfaces({ reported }: { reported?: Record<string, unknown> }) {
  const { t } = useTranslation();
  const rows = netifsOf(reported?.kinio_system as Record<string, unknown> | undefined);
  if (rows.length === 0) return null;
  return (
    <section className="ddev-section" id="netifs" data-ddev-section="netifs">
      <h2 className="section-title">{t('dhealth.netifs')}</h2>
      <div className="table-wrap">
        <table className="grid" data-testid="netifs">
          <thead>
            <tr>
              <th>{t('dhealth.if_name')}</th>
              <th>{t('dhealth.if_kind')}</th>
              <th>{t('dhealth.mac')}</th>
              <th>{t('dhealth.if_up')}</th>
              <th>IP</th>
              <th>IPv6</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.name} data-netif={r.name} data-up={r.up ? '1' : '0'}>
                <td>
                  <code>{r.name}</code>
                </td>
                <td>{r.kind ? t(`dhealth.if_kind_${r.kind}`) : '—'}</td>
                <td>{r.mac ? <code>{r.mac}</code> : '—'}</td>
                <td className={r.up ? undefined : 'muted'}>{r.up ? t('dhealth.if_up_yes') : t('dhealth.if_up_no')}</td>
                <td>{r.ip || '—'}</td>
                <td>{r.ip6 || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/**
 * 複製給客服（進階 › 技術；SPEC DEV-05、#vms-web-973「畫面不出現原始值」）：設備 ID、機種、韌體、媒體狀態、能力代碼
 * 只在這裡，一般畫面不出現。
 */
export function SupportInfo({ dev }: { dev: VmsDevice }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const lines = [
    `${t('common.device_id')}: ${dev.aid}`,
    `${t('devices.col_model')}: ${dev.model || '-'}`,
    `${t('devices.col_fw')}: ${dev.firmware || '-'}`,
    `${t('ddev.media_status')}: ${dev.mediaStatus || '-'}`,
    `${t('ddev.capabilities')}: ${dev.capabilities?.join(', ') || '-'}`,
  ];
  const text = lines.join('\n');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* 瀏覽器不給剪貼簿：使用者可以自己選取下面的文字 */
    }
  };
  return (
    <details className="card support-info" data-testid="support-info">
      <summary>{t('ddev.support_title')}</summary>
      <p className="muted small">{t('ddev.support_hint')}</p>
      <pre className="support-text">{text}</pre>
      <button type="button" className="btn btn-sm" onClick={() => void copy()}>
        {copied ? t('ddev.support_copied') : t('ddev.support_copy')}
      </button>
    </details>
  );
}

/**
 * 〔回報這台的問題〕（plan SPEC #support-business）：有回報權（owner / org_admin / org.support）才出現，開新回報並預設帶這台。
 */
export function ReportThisDevice({ dev }: { dev: VmsDevice }) {
  const { t } = useTranslation();
  const { orgs } = useScope();
  if (reporterOrgs(orgs).length === 0) return null;
  const qs = new URLSearchParams({ home: dev.homeId, aid: dev.aid, from: location.pathname });
  return (
    <p>
      <Link className="btn btn-sm" to={`/support/new?${qs}`} data-testid="report-this-device">
        {t('support.report_device')}
      </Link>
    </p>
  );
}

/** AI（DEV-05）：這台攝影機的 AI 運算指派（AI box / NVR NPU）；指派與調整在 AI 運算頁。 */
export function AiTab({ dev }: { dev: VmsDevice }) {
  const { t } = useTranslation();
  const q = useQuery({ queryKey: ['ai-providers', dev.homeId], queryFn: () => aiApi.providers(dev.apiEndpoint, dev.homeId), retry: false });
  const hits = (q.data ?? []).flatMap((p) => p.assignments.filter((a) => a.device_ref === dev.aid).map((a) => ({ p, a })));
  return (
    <div data-testid="dev-ai">
      {q.isLoading && <p className="muted">{t('common.loading')}</p>}
      {q.isError && <p className="muted">{t('ddev.ai_unavailable')}</p>}
      {q.isSuccess && hits.length === 0 && <p className="muted">{t('ddev.ai_none')}</p>}
      <ul className="plain-list">
        {hits.map(({ p, a }) => (
          <li key={a.assignment_id}>
            <strong>{p.name || t('ddev.ai_provider_unnamed')}</strong> · {a.models.map((m) => t(`ai.model.${m.model}`, { defaultValue: m.model })).join(t('common.list_sep'))} ·{' '}
            <span className="badge">{t(`ai.st_${a.status ?? 'pending'}`)}</span>
            {a.fps ? <span className="muted"> · {a.fps} fps</span> : null}
          </li>
        ))}
      </ul>
      <Link className="btn btn-sm" to="/ai">
        {t('ddev.ai_open')}
      </Link>
    </div>
  );
}

interface FwStatus {
  current_version?: string;
  update_available?: boolean;
  reason?: string;
  release?: { version?: string };
  settings?: { auto_update?: boolean; maintenance_window?: string; channel?: string };
}

const otaBase = (dev: VmsDevice) => `${planeBase(dev.apiEndpoint)}/v1/homes/${encodeURIComponent(dev.homeId)}/things/${encodeURIComponent(dev.aid)}/ota`;

/** 這台的韌體狀態（概覽摘要與健康與韌體分頁共用同一份快取）。 */
export function useFwStatus(dev: VmsDevice, enabled = true) {
  return useQuery({ queryKey: ['fw', dev.homeId, dev.aid], queryFn: () => kinioFetch<FwStatus>(otaBase(dev)), retry: false, enabled });
}

/** 韌體（DEV-05）：目前版本、可用更新、自動更新設定；owner 可「立即更新」（平台 OTA …/things/{device}/ota，Swann 設備走 App）。 */
export function FirmwareTab({ dev, owner }: { dev: VmsDevice; owner: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const base = otaBase(dev);
  const q = useFwStatus(dev);
  const [msg, setMsg] = useState<string | null>(null);
  const { ask, dialog } = useConfirm();
  const update = async () => {
    if (!(await ask({ title: t('ddev.fw_confirm', { v: q.data?.release?.version ?? '' }), action: t('common.action_update_fw') }))) return;
    try {
      const r = await kinioFetch<{ started?: boolean; delivery?: string }>(base + '/update', { method: 'POST' });
      setMsg(r?.delivery === 'app' ? t('ddev.fw_via_app') : t('ddev.fw_started'));
      void qc.invalidateQueries({ queryKey: ['fw', dev.homeId, dev.aid] });
    } catch (e) {
      const c = (e as { code?: string }).code;
      setMsg(c ? t(`dctl.err_${c}`, { defaultValue: t('common.error') }) : t('common.error'));
    }
  };
  const s = q.data;
  return (
    <div data-testid="dev-fw">
      {q.isLoading && <p className="muted">{t('common.loading')}</p>}
      {q.isError && <p className="muted">{t('ddev.fw_unavailable')}</p>}
      {s && (
        <table className="grid dinfo">
          <tbody>
            <tr>
              <th scope="row">{t('devices.col_fw')}</th>
              <td className="mono">{s.current_version || dev.firmware || '—'}</td>
            </tr>
            <tr>
              <th scope="row">{t('ddev.fw_available')}</th>
              <td>{s.update_available ? <span className="badge badge-warn">{s.release?.version ?? t('common.yes')}</span> : t('ddev.fw_latest')}</td>
            </tr>
            <tr>
              <th scope="row">{t('ddev.fw_auto')}</th>
              <td>
                {s.settings?.auto_update ? t('common.on') : t('common.off')}
                {s.settings?.maintenance_window ? ` · ${s.settings.maintenance_window}` : ''}
              </td>
            </tr>
          </tbody>
        </table>
      )}
      {owner && s?.update_available && (
        <button className="btn btn-primary" onClick={() => void update()}>
          {t('ddev.fw_update_now')}
        </button>
      )}
      {msg && <p className="small muted">{msg}</p>}
      <p>
        <Link to="/devices/firmware">{t('ddev.fw_all')}</Link>
      </p>
      {dialog}
    </div>
  );
}

/** 位置（DEV-05）：場域、地圖座標、平面圖上的擺放（擺放在地圖頁改）。 */
export function LocationTab({ dev }: { dev: VmsDevice }) {
  const { t } = useTranslation();
  const pathOf = useDevicePath();
  const { homes, orgs } = useScope();
  const home = homes.find((h) => h.home_id === dev.homeId);
  const org = orgs.find((o) => o.org_id === home?.org_id);
  const sites = useQuery({ queryKey: ['sites', org?.org_id], queryFn: () => orgApi.sites(org!), enabled: !!org, retry: false, staleTime: 60_000 });
  const geo = useQuery({ queryKey: ['geo-placements', dev.homeId], queryFn: () => vmsApi.geoPlacements(dev.apiEndpoint, dev.homeId), retry: false });
  const fps = useQuery({
    queryKey: ['floorplans', 'h:' + dev.homeId],
    queryFn: () => vmsApi.floorplans({ kind: 'home', api: dev.apiEndpoint, homeId: dev.homeId, label: '' }),
    retry: false,
  });
  const site = sites.data?.find((s) => s.site_id === home?.site_id);
  // 場域寫節點名稱與路徑（從組織名起算；根節點寫「<組織>（整個組織）」，不露根節點的內部名，第二輪 B2-23）
  const siteText = !site
    ? '—'
    : !site.parent_site_id
      ? t('ot.whole_org_name', { org: org?.display_name ?? site.name })
      : (siteNamePath(sites.data ?? [], site.site_id, org?.display_name ?? '') ?? [site.name]).join(PATH_SEP);
  const gp = geo.data?.data.find((p) => p.device_ref === dev.aid);
  const onPlans = (fps.data?.data ?? []).flatMap((f) => f.placements.filter((p) => p.device_ref === dev.aid).map((p) => ({ f, p })));
  return (
    <div data-testid="dev-location">
      <table className="grid dinfo">
        <tbody>
          {/* 一列就好：跟概覽的「所在位置」同一個路徑函式（第四輪 R4-P1-8：原本「家戶 / 場域：lab」「場域：EddyStudio（整個組織）」兩列讓人搞混） */}
          <tr>
            <th scope="row">{t('ddev.path')}</th>
            <td data-testid="dev-location-path" title={siteText !== '—' ? t('ddev.site') + '：' + siteText : undefined}>
              {pathOf(dev)}
            </td>
          </tr>
          <tr>
            <th scope="row">{t('ddev.geo')}</th>
            <td className="mono">{gp?.lat !== undefined && gp.lng !== undefined ? `${gp.lat.toFixed(6)}, ${gp.lng.toFixed(6)}` : '—'}</td>
          </tr>
          <tr>
            <th scope="row">{t('ddev.floorplan')}</th>
            <td>{onPlans.length ? onPlans.map(({ f }) => f.name).join(t('common.list_sep')) : '—'}</td>
          </tr>
        </tbody>
      </table>
      <Link className="btn btn-sm" to="/map">
        {t('ddev.open_map')}
      </Link>
    </div>
  );
}

/** 稽核（DEV-05、SPEC #vms-web-817）：企業家戶、有 org.audit 的人——org 稽核裡提到這台的紀錄（全文 q = device_ref），跟稽核頁同一個清單。 */
export function AuditTab({ dev }: { dev: VmsDevice }) {
  const { homes, orgs } = useScope();
  const org = orgs.find((o) => o.org_id === homes.find((h) => h.home_id === dev.homeId)?.org_id);
  return org ? <DevAudit org={org} aid={dev.aid} /> : null;
}

function DevAudit({ org, aid }: { org: MyOrg; aid: string }) {
  const names = useAuditNames(org);
  const q = useQuery({ queryKey: ['dev-audit', org.org_id, aid], queryFn: () => orgApi.audit(org, { q: aid }), retry: false });
  return (
    <div data-testid="dev-audit">
      {/* 設備頁只列最近 20 筆，其餘到稽核頁（帶這台的篩選；第二輪 R2-P1-4） */}
      <AuditRows q={q} events={q.data?.events ?? []} names={names} limit={20} more={`/audit?device=${encodeURIComponent(aid)}`} />
    </div>
  );
}

/** 稽核分頁出不出現：企業家戶、而且我在那個 org 有 org.audit。 */
export function useCanAudit(dev: VmsDevice): boolean {
  const { homes, orgs } = useScope();
  const org = orgs.find((o) => o.org_id === homes.find((h) => h.home_id === dev.homeId)?.org_id);
  return !!org && hasOrgPerm(org, 'org.audit');
}
